import { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Dashboard } from './pages/Dashboard';
import { NewGame } from './pages/NewGame';
import { Workspace } from './pages/Workspace';
import { Settings } from './pages/Settings';
import { Devices } from './pages/Devices';
import { UpdateBanner } from './components/UpdateBanner';
import { WhatsNewCard } from './components/WhatsNewCard';
import { AppFrameProvider, useAppFrame, type AppFrame } from './AppFrameContext';
import {
  CrucibleClientProvider,
  useClientCapabilities,
  useCrucibleClient,
} from './transport/context';
import type { CrucibleClient } from '@volley/crucible-client';
import { scopeCloudProjectQueries } from './transport/project-query-key';
import { ProjectAccessDialog } from './components/ProjectAccessDialog';
import type { ProjectCardAvailability } from '@volley/crucible-ipc-types/ipc-channels';

export function appRootClassName(frame: AppFrame = 'fullscreen'): string {
  const sizeClass = frame === 'fullscreen' ? 'h-screen w-screen' : 'h-full w-full';
  return `flex ${sizeClass} flex-col overflow-hidden bg-crucible-bg text-crucible-fg`;
}

export function assertProjectRuntimeReady(snapshot: { availability: string }): void {
  if (snapshot.availability !== 'available') {
    throw new Error(`Project runtime is not ready (${snapshot.availability}).`);
  }
}

export function ProjectWorkspaceRoute(): React.JSX.Element {
  const { placement, gameId } = useParams<{ placement: 'local' | 'cloud'; gameId: string }>();
  const capabilities = useClientCapabilities();
  const client = useCrucibleClient();
  const navigate = useNavigate();
  const [workspaceClient, setWorkspaceClient] = useState<CrucibleClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<{
    availability: Exclude<ProjectCardAvailability, 'available'>;
    reason?: string;
  } | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  useEffect(() => {
    setWorkspaceClient(null);
    setError(null);
    setUnavailable(null);
    if (!gameId || (placement !== 'local' && placement !== 'cloud')) {
      setError('Invalid project route.');
      return;
    }
    if (capabilities.projectPlacementAuthority === 'cloud-runtime') {
      if (placement !== (capabilities.defaultProjectPlacement ?? 'cloud')) {
        setError('This runtime cannot open a project from that placement.');
        return;
      }
      setWorkspaceClient(scopeCloudProjectQueries(client, { placement, gameId }));
      return;
    }
    let cancelled = false;
    window.crucible
      .invoke('project-runtime:bind', { placement, gameId })
      .then((binding) => {
        if (binding.availability !== 'available') {
          if (!cancelled) {
            setUnavailable({
              availability: binding.availability,
              reason:
                binding.capabilities.agent.reason ??
                `This Cloud project is ${binding.availability}.`,
            });
          }
          return;
        }
        if (!cancelled) setWorkspaceClient(client.scopeProject?.(binding) ?? null);
      })
      .catch(
        (cause: unknown) =>
          !cancelled && setError(cause instanceof Error ? cause.message : String(cause)),
      );
    return () => {
      cancelled = true;
    };
  }, [
    capabilities.defaultProjectPlacement,
    capabilities.projectPlacementAuthority,
    client,
    placement,
    gameId,
    retryNonce,
  ]);
  if (unavailable && placement === 'cloud' && gameId) {
    return (
      <ProjectAccessDialog
        project={{ placement, gameId }}
        availability={unavailable.availability}
        reason={unavailable.reason}
        onClose={() => navigate('/dashboard')}
        onRetry={() => setRetryNonce((value) => value + 1)}
        onSignIn={() => {
          void window.crucible
            .invoke('cloud-account:sign-in')
            .then(() => setRetryNonce((value) => value + 1));
        }}
      />
    );
  }
  if (error) return <div className="p-8 text-sm text-red-300">{error}</div>;
  if (!workspaceClient) return <div className="p-8 text-sm text-white/50">Opening project…</div>;
  return (
    <CrucibleClientProvider client={workspaceClient}>
      <Workspace />
    </CrucibleClientProvider>
  );
}

/**
 * Resolves an unqualified legacy workspace URL to its placement-aware route.
 *
 * Args:
 *   None.
 *
 * Returns:
 *   A loading or error state until placement resolves, then a redirect.
 *
 * Raises:
 *   Never. Catalogue failures are rendered as route errors.
 */
export function LegacyWorkspaceRedirect(): React.JSX.Element {
  const { gameId } = useParams<{ gameId: string }>();
  const frame = useAppFrame();
  const [placement, setPlacement] = useState<'local' | 'cloud' | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setPlacement(null);
    setError(null);
    // Reject malformed routes before querying either host catalogue.
    if (!gameId) {
      setError('Invalid project route.');
      return;
    }
    // The authenticated browser shell has only one possible project placement.
    if (frame === 'embedded') {
      setPlacement('cloud');
      return;
    }
    let cancelled = false;
    // Placement discovery must remain global because workspace authority is bound after redirect.
    //
    // #3734: this used to ask `game:list`, whose handler walks git, Bifrost and
    // registry status for EVERY project. Both of those are `fetch` calls bounded
    // at `AbortSignal.timeout(5000)`, and the Bifrost one is awaited before the
    // per-game work starts — so with no route to either host (offline, VPN off)
    // the user watched "Opening project…" for up to ~10s, to answer a question
    // that needs no network whatsoever. It also made two legacy-UI e2e specs
    // fail whenever CI's runner couldn't reach Bifrost (#3687).
    //
    // We only need "is this id local or cloud". `game:list:local-lite` is the
    // explicitly network-free lister (no contributors, no status), so ask it
    // first and only reach for the cloud catalogue when the id isn't local —
    // which preserves the old local-preferred tie-break below.
    // Placement comes off the matched entry, never inferred from which channel
    // answered — and the local-preferred tie-break is applied within each
    // catalogue, so a list carrying both placements resolves the same way it
    // did when a single merged `game:list` served this.
    const pickPlacement = (
      projects: readonly { gameId: string; placement: 'local' | 'cloud' }[],
    ): 'local' | 'cloud' | undefined => {
      const matches = projects.filter((project) => project.gameId === gameId);
      return (matches.find((candidate) => candidate.placement === 'local') ?? matches[0])
        ?.placement;
    };
    const resolvePlacement = async (): Promise<'local' | 'cloud'> => {
      const localProjects = await window.crucible.invoke('game:list:local-lite');
      const local = pickPlacement(localProjects);
      if (local) return local;
      const cloudProjects = await window.crucible.invoke('game:list:cloud-lite', localProjects);
      const cloud = pickPlacement(cloudProjects);
      if (cloud) return cloud;
      throw new Error(`Project "${gameId}" is not present in the host catalogue.`);
    };
    resolvePlacement()
      .then((resolved) => {
        if (!cancelled) setPlacement(resolved);
      })
      .catch(
        (cause: unknown) =>
          !cancelled && setError(cause instanceof Error ? cause.message : String(cause)),
      );
    return () => {
      cancelled = true;
    };
  }, [frame, gameId]);
  if (error) return <div className="p-8 text-sm text-red-300">{error}</div>;
  if (!placement) return <div className="p-8 text-sm text-white/50">Opening project…</div>;
  return <Navigate to={`/workspace/${placement}/${gameId ?? ''}`} replace />;
}

/** Fake macOS traffic-light buttons for the browser preview.
 *  Renders only when the mock IPC bridge is active (i.e. not real Electron).
 *  Positioned to match the Electron trafficLightPosition: { x: 24, y: 31 }. */
export function MockTrafficLights(): React.JSX.Element | null {
  // Real Electron exposes electronAPI; the mock bridge sets a sentinel flag
  const isMock =
    typeof (window as unknown as { __reactErrors?: unknown }).__reactErrors !== 'undefined' &&
    !(window as unknown as { electronAPI?: unknown }).electronAPI;
  if (!isMock) return null;
  return (
    <div className="fixed z-[9999] flex items-center gap-2" style={{ left: 24, top: 31 }}>
      <div className="h-3.5 w-3.5 rounded-full bg-[#FF5F57]" />
      <div className="h-3.5 w-3.5 rounded-full bg-[#FEBC2E]" />
      <div className="h-3.5 w-3.5 rounded-full bg-[#28C840]" />
    </div>
  );
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
    },
  },
});

/** Apply theme and feature-flag CSS classes to document root. */
function ThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { data: config } = useQuery({
    queryKey: ['config:get'],
    queryFn: () => window.crucible.invoke('config:get'),
    staleTime: 60_000,
  });

  const { data: flags } = useQuery({
    queryKey: ['app:get-feature-flags'],
    queryFn: () => window.crucible.invoke('app:get-feature-flags'),
    staleTime: 60_000,
  });

  useEffect(() => {
    const theme = config?.theme ?? 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, [config?.theme]);

  useEffect(() => {
    if (flags?.newUi) {
      document.documentElement.classList.add('new-ui');
    } else if (flags !== undefined) {
      document.documentElement.classList.remove('new-ui');
    }
  }, [flags?.newUi, flags]);

  return <>{children}</>;
}

export function App({ frame = 'fullscreen' }: { frame?: AppFrame } = {}): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppFrameProvider frame={frame}>
          <HashRouter>
            <div className={appRootClassName(frame)}>
              <MockTrafficLights />
              <UpdateBanner fromAppShell />
              {/* Consumer/Prosumer tier switch lives inline in the mode-pill
              bar next to Build/Design/Art (NewGame.tsx) and
              Build/Design/Art (WorkspaceHeader.tsx) — see TierPillToggle.
              Not mounted here: Dashboard/Settings/Devices have no
              mode-pill bar to join and don't gate anything on tier. See
              Settings.tsx's PreferencesPanel for the fully
              host-capability-gated version of the same setting. */}
              {/* "What's new" card — mounted at the App shell so it surfaces
              regardless of which route the user lands on (Dashboard,
              Workspace, Settings, Devices). The component self-suppresses
              when the main process returns null (fresh install, no notes
              for this version, already dismissed). Floats top-right so it
              doesn't displace any route's layout. See issue #293. */}
              <WhatsNewCard />
              <div className="flex-1 overflow-hidden">
                <Routes>
                  {/* /onboarding (pages/Onboarding.tsx) was an unreachable second
                  onboarding flow that the audit flagged as dead code (see
                  docs/board-member-audit-current-state.md §1, "Other
                  landmines"). The reachable onboarding is the
                  <OnboardingOverlay/> that Dashboard.tsx mounts on first
                  launch — this PR removes the duplicate to stop the two
                  diverging. */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/new-game" element={<NewGame />} />
                  <Route path="/workspace/:placement/:gameId" element={<ProjectWorkspaceRoute />} />
                  <Route path="/workspace/:gameId" element={<LegacyWorkspaceRedirect />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/devices" element={<Devices />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </div>
          </HashRouter>
        </AppFrameProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
